import { createColumnHelper } from "@tanstack/react-table"
import type { ColumnDef } from "@tanstack/react-table"
import type { EngagementHoursRow } from "@/types/report"

const columnHelper = createColumnHelper<EngagementHoursRow>()

export const engagementHoursColumns: ColumnDef<EngagementHoursRow, any>[] = [
  // An identifier that happens to be numeric — summing it is meaningless
  columnHelper.accessor("userProfileId", {
    header: "Profile ID",
    size: 120,
    meta: { type: "integer", excludeFromGroupTotal: true },
    enableHiding: true,
  }),
  columnHelper.accessor("workerName", {
    header: "Worker Name",
    size: 200,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("workerType", {
    header: "Worker Type",
    size: 150,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("engagementCode", {
    header: "Engagement Code",
    size: 170,
    meta: { type: "string" },
  }),
  // GW-231: groups an engagement together with its change-request siblings, under the chain root.
  // Offered in addition to grouping by Engagement Code — this report is one row per worker per
  // engagement, so that grouping is already meaningful and stays available.
  // A grouping key rather than a data column, so it is hidden from the grid body by default.
  columnHelper.accessor("rollUpEngagement", {
    header: "Roll-up",
    size: 240,
    meta: { type: "string" },
    enableHiding: true,
  }),
  // The worker's own start/end on this engagement, not the engagement's own dates: the row is one
  // worker on one engagement. Headers match Time Entries; People names its wider per-worker span
  // "First Engagement Start Date" / "Last Engagement End Date". "Engagement Start Date" is already
  // what the engagement Workers dialog calls a worker's firstDayOfWork. A worker with no last day
  // set shows the engagement's end, which is what leaving it blank means there.
  columnHelper.accessor("engagementStartDate", {
    header: "Engagement Start Date",
    size: 210,
    meta: { type: "date", dateRangePair: "engagementEndDate" },
  }),
  columnHelper.accessor("engagementEndDate", {
    header: "Engagement End Date",
    size: 210,
    meta: { type: "date", dateRangePair: "engagementStartDate" },
  }),
  columnHelper.accessor("billingType", {
    header: "Billing Type",
    size: 140,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("plannedHours", {
    header: "Planned Hours",
    size: 150,
    meta: { type: "number" },
  }),
  columnHelper.accessor("actualHours", {
    header: "Actual Hours",
    size: 150,
    meta: { type: "number" },
  }),
  columnHelper.accessor("varianceHours", {
    header: "Variance (Hours)",
    size: 160,
    meta: { type: "number" },
  }),
  // Recomputed for a group total from the summed parts, mirroring spEngagementHoursReport's
  // ((Actual - Planned) / Planned) * 100 — Variance (Hours) is already that numerator.
  columnHelper.accessor("variancePercent", {
    header: "Variance (%)",
    size: 140,
    meta: {
      type: "percent",
      groupTotalRatio: { numerator: "varianceHours", denominator: "plannedHours" },
    },
  }),
  columnHelper.accessor("billableHours", {
    header: "Billable Hours",
    size: 150,
    meta: { type: "number" },
  }),
  columnHelper.accessor("nonBillableHours", {
    header: "Non-Billable Hours",
    size: 175,
    meta: { type: "number" },
  }),
  // The SP divides these by TotalHours, which is the same value it reports as Actual Hours
  columnHelper.accessor("billableTotalPercent", {
    header: "Billable/Total %",
    size: 160,
    meta: {
      type: "percent",
      groupTotalRatio: { numerator: "billableHours", denominator: "actualHours" },
    },
  }),
  columnHelper.accessor("nonBillableTotalPercent", {
    header: "Non-Billable/Total %",
    size: 185,
    meta: {
      type: "percent",
      groupTotalRatio: { numerator: "nonBillableHours", denominator: "actualHours" },
    },
  }),
  columnHelper.accessor("jobTitle", {
    header: "Job Title",
    size: 180,
    meta: { type: "string" },
  }),
  columnHelper.accessor("functionalManager", {
    header: "Functional Manager",
    size: 190,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("engagementLead", {
    header: "Engagement Lead",
    size: 180,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("deliveryManager", {
    header: "Delivery Manager",
    size: 180,
    meta: { type: "string", uniqueValues: true },
  }),
]

export const defaultHiddenColumns: Record<string, boolean> = {
  userProfileId: false,
  rollUpEngagement: false,
}
